import { useState } from "react";
import { useTranslation } from "react-i18next";

import { OptionGrid } from "../../components/game/OptionGrid.js";
import type { AnswerView, RoundView, YouView } from "../../services/generated/client.js";

/**
 * The two grids and the lock — who is it, and what is it.
 *
 * # Live while the clip is still playing
 *
 * Phase 14 C4, and it is the change that made the game stop feeling slow. The
 * engine takes a guess in `Listening` as well as `Guessing`, so a grid that
 * stays greyed until the audio ends is the same wait with the reason hidden:
 * somebody who knew the song in four seconds sat through another twenty-six.
 * `live` is what the round screen decides; nothing here waits for anything.
 *
 * # It renders the answer's *position*, never its year
 *
 * `correctOption` comes from `RoundView::answer`, which the server omits
 * entirely before `Reveal` (Critical Rule 7). There is nothing to hide here
 * because there is nothing here to hide — and that is the property to keep: ask
 * for the narrow thing, not the round.
 *
 * # Changing your mind must land — phase 18, finding #2
 *
 * A player tapped an option, changed their mind, tapped another, and the second
 * tap did nothing: the round screen set `busy` for the whole round trip and both
 * grids were rendered `disabled={!live || busy}`, so the *rapid second tap* —
 * which is what changing your mind is — landed inside that window and was
 * swallowed with no error, no refusal and nothing on screen. It showed up at the
 * reveal as `Falló … artista` against somebody who believed they had answered
 * something else.
 *
 * **The engine was never the problem.** `Engine::guess` overwrites the option on
 * every call and the runner publishes even for an effect-less guess, so a change
 * would have reached the phone if one had ever been sent.
 *
 * So the selection is optimistic: the tap paints immediately and the newest
 * choice is what goes to the server. The overlay is cleared when the server
 * confirms the same value, and **cleared on a refusal** so a rejected tap snaps
 * back to what the server actually holds rather than lying about it. `busy`
 * still disables the *lock*, which is genuinely once-only.
 *
 * # The lock is not here any more — phase 18
 *
 * It lives in the stepper's sticky action bar, because that is the one control
 * the phase has and it must be reachable without scrolling (finding #12). Phase
 * 14 B2's rule survives the move intact: the bar's button is **disabled until
 * something is selected**, so it still cannot be used to lock an empty guess.
 */
export function GuessPanel({
  round,
  you,
  answer,
  live,
  locked = false,
  onGuess,
}: {
  round: RoundView;
  you: YouView | null;
  /** Present only from `Reveal`. */
  answer: AnswerView | null;
  /** Whether this player may answer right now. */
  live: boolean;
  /**
   * Whether this player has locked, which is now final (phase 18, finding #6).
   *
   * Distinct from `!live`: not being able to answer *yet* and having said *"stop
   * waiting for me"* are different states, and only one of them deserves a
   * sentence saying the decision is made.
   */
  locked?: boolean;
  /**
   * Both grids at once — either may be left alone.
   *
   * Returns a promise so a refusal can drop the optimistic selection: the grid
   * must never keep showing a choice the server declined.
   */
  onGuess: (artistOption: number | null, titleOption: number | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  // What this player has tapped but the server has not confirmed yet. `null`
  // means "no opinion, show what the server holds" — which is different from a
  // deliberate `null` option, and is why these are two states rather than one
  // nullable pair.
  const [pendingArtist, setPendingArtist] = useState<number | null>(null);
  const [pendingTitle, setPendingTitle] = useState<number | null>(null);

  const serverArtist = you?.artist_option ?? null;
  const serverTitle = you?.title_option ?? null;

  // **The overlay outlives the round trip and nothing else.** It is dropped on a
  // refusal, and the round screen mounts this panel with `key={round_no}`, so a
  // new round starts from the server's truth rather than from a tap somebody
  // made a card ago.
  //
  // Nothing clears it on confirmation, because within a round nothing can move
  // the submission but this player: `Engine::guess` only ever writes what was
  // sent, and `narrow_options` strikes an *option* out without touching the
  // answer — a selected option that gets struck renders struck and still
  // selected, which is the truth. So `pending === server` is a value the two
  // agree on and there is nothing to correct.
  const shownArtist = pendingArtist ?? serverArtist;
  const shownTitle = pendingTitle ?? serverTitle;

  return (
    <>
      <OptionGrid
        label={t("tally.artist")}
        options={round.artist_options}
        selected={shownArtist}
        removed={you?.removed_artist_options ?? []}
        correctOption={answer?.artist_option ?? null}
        // **Not `|| busy`.** That is finding #2: a second tap while the first is
        // in flight is exactly what changing your mind looks like.
        disabled={!live}
        {...(live
          ? {
              onSelect: (index: number) => {
                setPendingArtist(index);
                void onGuess(index, shownTitle).catch(() => {
                  setPendingArtist(null);
                });
              },
            }
          : {})}
      />

      <OptionGrid
        label={t("tally.title")}
        options={round.title_options}
        selected={shownTitle}
        removed={you?.removed_title_options ?? []}
        correctOption={answer?.title_option ?? null}
        disabled={!live}
        {...(live
          ? {
              onSelect: (index: number) => {
                setPendingTitle(index);
                void onGuess(shownArtist, index).catch(() => {
                  setPendingTitle(null);
                });
              },
            }
          : {})}
      />

      {locked && !answer && (
        // Said once the lock has landed, because a lock is final and a screen
        // that simply greys out is indistinguishable from one that is waiting.
        <p role="status" className="text-xs text-white/50">
          {t("round.lockedIn")}
        </p>
      )}
    </>
  );
}
